import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import WebpagesFolderTree from './WebpagesFolderTree.jsx';
import WebpagesFolderContextMenu from './WebpagesFolderContextMenu.jsx';
import WebpagesPageContextMenu from './WebpagesPageContextMenu.jsx';
import WebpagesPagesPanelContextMenu from './WebpagesPagesPanelContextMenu.jsx';
import WebpagesBulkActionsModal from './WebpagesBulkActionsModal.jsx';
import FileUploader from '../file_uploader/FileUploader.jsx';
import AttachmentListView from '../attachments/AttachmentListView.jsx';
import AttachmentEditor from '../attachments/AttachmentEditor.jsx';
import AttachmentPreviewModal from '../attachments/AttachmentPreviewModal.jsx';
import { getAttachmentMediaType, getAttachmentPreviewUrl } from '../attachments/helpers';
import API_ENDPOINTS from '../ApiEndPoints';

const api = {
  foldersTree: '/api/v1/folders/tree',
  browserItems: '/api/v1/folders/browser-items',
  uploadBucket: '/api/v1/folders/upload-bucket',
  folderUploadBucket: (folderId) => `/api/v1/folders/${folderId}/upload-bucket`,
  attachUploadedDocuments: (folderId) => `/api/v1/folders/${folderId}/attach-uploaded-documents`,
  detachDocuments: (folderId) => `/api/v1/folders/${folderId}/detach-documents`,
  folderDocuments: (folderId) => `/api/v1/folders/${folderId}/documents`,
  allDocuments: '/api/v1/folders/documents',
  bulkAction: '/api/v1/folders/bulk-action',
  moveItemsToFolder: '/api/v1/folders/move-items',
  createFolder: '/api/v1/folders',
  renameFolder: (id) => `/api/v1/folders/${id}`,
  moveFolder: (id) => `/api/v1/folders/${id}/move`,
  copyFolder: (id) => `/api/v1/folders/${id}/copy`,
};

function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function formatDateTime(value) {
  if (!value) return '';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
  } catch {
    return String(value);
  }
}

export default function WebpagesBrowserDev() {
  const containerRef = useRef(null);
  const folderEditorInputRef = useRef(null);

  const [folders, setFolders] = useState([]);
  const [selectedNode, setSelectedNode] = useState({ id: 'all', isAll: true, title: 'All' });
  const [selectedKey, setSelectedKey] = useState('all');

  const [pages, setPages] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [loadingPages, setLoadingPages] = useState(false);
  const [error, setError] = useState('');

  const [uploadInfo, setUploadInfo] = useState({ loading: false, templateId: null, folderDefinitionId: null });

  const [folderDocs, setFolderDocs] = useState([]);
  const [folderDocsLoading, setFolderDocsLoading] = useState(false);
  const [folderDocsError, setFolderDocsError] = useState('');
  const [folderDocsPage, setFolderDocsPage] = useState(1);
  const [folderDocsPerPage, setFolderDocsPerPage] = useState(25);
  const [folderDocsPagination, setFolderDocsPagination] = useState({ page: 1, per_page: 25, total: 0, last_page: 1 });
  const [folderDocsContext, setFolderDocsContext] = useState(null);
  const [folderDocSelected, setFolderDocSelected] = useState(null);
  const [folderDocEditorOpen, setFolderDocEditorOpen] = useState(false);
  const [folderDocSaving, setFolderDocSaving] = useState(false);
  const [folderDocPreviewAttachment, setFolderDocPreviewAttachment] = useState(null);
  const [folderDocPreviewOpen, setFolderDocPreviewOpen] = useState(false);

  const [rootDocs, setRootDocs] = useState([]);
  const [rootDocsLoading, setRootDocsLoading] = useState(false);
  const [rootDocsError, setRootDocsError] = useState('');
  const [rootDocsPage, setRootDocsPage] = useState(1);
  const [rootDocsPerPage, setRootDocsPerPage] = useState(25);
  const [rootDocsPagination, setRootDocsPagination] = useState({ page: 1, per_page: 25, total: 0, last_page: 1 });
  const [rootDocsContext, setRootDocsContext] = useState(null);

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('title');
  const [sortDir, setSortDir] = useState('asc');

  const [showTagsColumn, setShowTagsColumn] = useState(false);
  const [showCreatedColumn, setShowCreatedColumn] = useState(false);

  const [rightTab, setRightTab] = useState('definitions');
  const [docsView, setDocsView] = useState(() => {
    try {
      const stored = window.localStorage.getItem('webpages_browser_docs_view');
      if (stored === 'grid' || stored === 'list') return stored;
      return 'list';
    } catch {
      return 'list';
    }
  });

  const [itemsView, setItemsView] = useState(() => {
    try {
      const stored = window.localStorage.getItem('webpages_browser_items_view');
      if (stored === 'grid' || stored === 'list') return stored;
      return 'list';
    } catch {
      return 'list';
    }
  });

  const [isNarrow, setIsNarrow] = useState(false);
  const [foldersCollapsed, setFoldersCollapsed] = useState(false);
  const [splitPct, setSplitPct] = useState(() => {
    try {
      const stored = window.localStorage.getItem('webpages_browser_split_pct');
      const parsed = stored ? Number(stored) : NaN;
      if (!Number.isFinite(parsed)) return 38;
      return Math.min(50, Math.max(25, parsed));
    } catch {
      return 38;
    }
  });

  const [menuState, setMenuState] = useState({ visible: false, x: 0, y: 0, node: null });
  const [folderClipboard, setFolderClipboard] = useState(null); // { mode: 'copy'|'cut', node }

  // Inline folder editor (no browser prompt modals)
  // { mode: 'new'|'rename', folderId?: number, parentId?: number, label: string }
  const [folderEditor, setFolderEditor] = useState(null);
  const [folderEditorTitle, setFolderEditorTitle] = useState('');
  const [folderEditorBusy, setFolderEditorBusy] = useState(false);

  const [pageMenuState, setPageMenuState] = useState({ visible: false, x: 0, y: 0, page: null });
  const [pagesPanelMenuState, setPagesPanelMenuState] = useState({ visible: false, x: 0, y: 0 });
  const [pageClipboard, setPageClipboard] = useState(null); // { mode: 'copy'|'cut', pageIds:[] }

  const [dragStatus, setDragStatus] = useState(null); // { subject, action, target }
  const [selectedPageIds, setSelectedPageIds] = useState([]);
  const [bulkActionModal, setBulkActionModal] = useState({ isOpen: false, mode: null });
  const [deleteConfirmArmed, setDeleteConfirmArmed] = useState(false);

  const selectedKeyValue = useMemo(() => selectedKey || String(selectedNode?.nodeKey || selectedNode?.id || 'all'), [selectedKey, selectedNode]);

  const filteredSortedPages = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = (pages || []).filter((p) => {
      if (!q) return true;
      const title = String(p.title || '').toLowerCase();
      const tid = String(p.tid || '').toLowerCase();
      const kind = String(p.kind || p.subclass || '').toLowerCase();
      const typeId = String(p.type_id || '').toLowerCase();
      const tags = String(p.tags || '').toLowerCase();
      return title.includes(q) || tid.includes(q) || kind.includes(q) || typeId.includes(q) || tags.includes(q);
    });

    const dir = sortDir === 'desc' ? -1 : 1;
    const getVal = (p) => {
      if (sortKey === 'tid') return String(p.tid || '');
      if (sortKey === 'active') return String(p.active || '0');
      if (sortKey === 'updated_at') return String(p.updated_at || '');
      return String(p.title || '');
    };

    return filtered.slice().sort((a, b) => getVal(a).localeCompare(getVal(b)) * dir);
  }, [pages, search, sortKey, sortDir]);

  const debouncedSearch = useDebouncedValue(search, 250);

  const loadFolders = useCallback(async () => {
    setLoadingFolders(true);
    setError('');
    try {
      const { data } = await axios.get(api.foldersTree);
      if (data && data.success) {
        setFolders(data.data || []);
      } else {
        setFolders([]);
      }
    } catch {
      setError('Failed to load folders');
      setFolders([]);
    } finally {
      setLoadingFolders(false);
    }
  }, []);

  const loadPagesForNode = useCallback(async (node) => {
    setLoadingPages(true);
    setError('');
    try {
      const isVirtualAll = !node || node.isAll || node.isTypeRoot || String(node.id) === 'all';
      const filterTypeIds = Array.isArray(node?.filterTypeIds) ? node.filterTypeIds : null;
      const filterKinds = Array.isArray(node?.filterKinds) ? node.filterKinds : null;

      const params = {
        ...(isVirtualAll ? {} : { folder_id: node.id }),
        ...(filterTypeIds && filterTypeIds.length ? { type_ids: filterTypeIds.join(',') } : {}),
      };

      const { data } = await axios.get(api.browserItems, { params });
      const raw = (data && data.success && data.data) ? data.data : [];
      const filtered = filterKinds && filterKinds.length
        ? raw.filter((it) => filterKinds.includes(String(it.kind || '').toLowerCase()))
        : raw;
      setPages(filtered);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load items');
      setPages([]);
    } finally {
      setLoadingPages(false);
    }
  }, []);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    loadPagesForNode(selectedNode);
  }, [loadPagesForNode, selectedNode]);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth || 0;
      const nextIsNarrow = w < 992;
      setIsNarrow(nextIsNarrow);
      setFoldersCollapsed(nextIsNarrow);
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem('webpages_browser_split_pct', String(splitPct));
    } catch {
      // ignore
    }
  }, [splitPct]);

  useEffect(() => {
    try {
      window.localStorage.setItem('webpages_browser_items_view', String(itemsView || 'list'));
    } catch {
      // ignore
    }
  }, [itemsView]);

  useEffect(() => {
    try {
      window.localStorage.setItem('webpages_browser_docs_view', String(docsView || 'list'));
    } catch {
      // ignore
    }
  }, [docsView]);

  const handleSelect = useCallback((node) => {
    setSelectedNode(node);
    setSelectedKey(String(node?.nodeKey || node?.id || 'all'));
    setSelectedPageIds([]);
    setDeleteConfirmArmed(false);
    setFolderEditor(null);

    // When selecting the system Media node, default to Instances (it contains the items).
    // This is intentionally a one-time selection default; users can still click back to Definitions.
    const isMediaTypeNode = (() => {
      if (!node) return false;
      if (String(node.id) === 'type_documents') return true;
      if (!node.isTypeRoot) return false;
      const typeIds = Array.isArray(node.filterTypeIds) ? node.filterTypeIds.map(String) : [];
      return typeIds.includes('9');
    })();
    if (isMediaTypeNode) {
      setRightTab('instances');
    }
  }, []);

  const isRealFolderNode = useMemo(() => {
    if (!selectedNode) return false;
    if (selectedNode.isAll || selectedNode.isRoot || selectedNode.isTypeRoot) return false;
    if (String(selectedNode.id) === 'all' || String(selectedNode.id) === 'root') return false;
    return Number.isFinite(Number(selectedNode.id));
  }, [selectedNode]);

  const isDocumentsTypeNode = useMemo(() => {
    if (!selectedNode) return false;
    if (String(selectedNode.id) === 'type_documents') return true;
    if (!selectedNode.isTypeRoot) return false;
    const typeIds = Array.isArray(selectedNode.filterTypeIds) ? selectedNode.filterTypeIds.map(String) : [];
    return typeIds.includes('9');
  }, [selectedNode]);

  const showInstancesTab = useMemo(() => isRealFolderNode || isDocumentsTypeNode, [isDocumentsTypeNode, isRealFolderNode]);

  useEffect(() => {
    if (!showInstancesTab && rightTab !== 'definitions') {
      setRightTab('definitions');
    }
  }, [rightTab, showInstancesTab]);

  useEffect(() => {
    const loadUploadInfo = async () => {
      if (!isRealFolderNode) {
        setUploadInfo({ loading: false, templateId: null, folderDefinitionId: null });
        return;
      }

      setUploadInfo({ loading: true, templateId: null, folderDefinitionId: null });
      try {
        const { data } = await axios.get(api.folderUploadBucket(selectedNode.id));
        if (data && data.success && data.data) {
          setUploadInfo({ loading: false, templateId: data.data.template_id, folderDefinitionId: data.data.folder_definition_id });
          return;
        }
      } catch {
        // ignore
      }
      setUploadInfo({ loading: false, templateId: null, folderDefinitionId: null });
    };

    loadUploadInfo();
  }, [isRealFolderNode, selectedNode?.id]);

  const openNewRootFolderEditor = useCallback(() => {
    setFolderEditor({
      mode: 'new',
      parentId: null,
      label: 'New root folder',
    });
    setFolderEditorTitle('');
  }, []);

  useEffect(() => {
    if (!folderEditor) return;
    // Focus the inline input when editor is opened.
    const t = window.setTimeout(() => {
      if (folderEditorInputRef.current) {
        folderEditorInputRef.current.focus();
        folderEditorInputRef.current.select();
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, [folderEditor]);

  const closeFolderEditor = useCallback(() => {
    if (folderEditorBusy) return;
    setFolderEditor(null);
    setFolderEditorTitle('');
  }, [folderEditorBusy]);

  const submitFolderEditor = useCallback(async () => {
    if (!folderEditor || folderEditorBusy) return;
    const title = (folderEditorTitle || '').trim();
    if (!title) return;

    setError('');
    setFolderEditorBusy(true);
    try {
      if (folderEditor.mode === 'new') {
        await axios.post(api.createFolder, { title, parent_id: folderEditor.parentId });
      } else if (folderEditor.mode === 'rename') {
        await axios.put(api.renameFolder(folderEditor.folderId), { title });
      }
      await loadFolders();
      closeFolderEditor();
    } catch (e) {
      setError(e?.response?.data?.error || 'Folder update failed');
    } finally {
      setFolderEditorBusy(false);
    }
  }, [closeFolderEditor, folderEditor, folderEditorBusy, folderEditorTitle, loadFolders]);

  const handleSort = useCallback((key) => {
    setSortKey((prev) => {
      if (prev !== key) {
        setSortDir('asc');
        return key;
      }
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return prev;
    });
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragStatus(null);
  }, []);

  const handleDragHover = useCallback((node, isHovering) => {
    setDragStatus((prev) => {
      if (!prev) return null;
      return { ...prev, target: isHovering ? node.title : '...' };
    });
  }, []);

  const handleDragStartItem = useCallback((e, item) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';

    let idsToMove = [item.id];
    if (String(item.type_id) === '13' && selectedPageIds.includes(item.id)) {
      idsToMove = selectedPageIds;
    }

    e.dataTransfer.setData('item-ids', idsToMove.join(','));
    setDragStatus({ subject: `${idsToMove.length} Item(s)`, action: 'Moving to', target: '...' });
  }, [selectedPageIds]);

  const handleDragStartDocumentInstance = useCallback((e, attachment) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    const idsToMove = [Number(attachment?.id)].filter(Boolean);
    if (!idsToMove.length) return;
    e.dataTransfer.setData('document-instance-ids', idsToMove.join(','));
    setDragStatus({ subject: `${idsToMove.length} Document(s)`, action: 'Attaching to', target: '...' });
  }, []);

  const handleDropItems = useCallback(async (folderId, itemIds) => {
    if (!folderId || !itemIds || !itemIds.length) return;
    setError('');
    try {
      await axios.post(api.moveItemsToFolder, {
        target_folder_id: folderId,
        item_ids: itemIds,
      });
      await loadFolders();
      await loadPagesForNode(selectedNode);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to move page(s)');
    } finally {
      setDragStatus(null);
    }
  }, [loadFolders, loadPagesForNode, selectedNode]);

  const canUploadDocumentsHere = useMemo(() => isRealFolderNode, [isRealFolderNode]);

  const loadRootDocuments = useCallback(async () => {
    if (!isDocumentsTypeNode) {
      setRootDocs([]);
      setRootDocsPagination({ page: 1, per_page: rootDocsPerPage, total: 0, last_page: 1 });
      setRootDocsContext(null);
      setRootDocsError('');
      return;
    }

    setRootDocsLoading(true);
    setRootDocsError('');
    try {
      const params = {
        page: rootDocsPage,
        per_page: rootDocsPerPage,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        sort: 'created_at',
        direction: 'desc',
      };
      const { data } = await axios.get(api.allDocuments, { params });
      setRootDocs(data?.data || []);
      setRootDocsPagination(data?.pagination || { page: rootDocsPage, per_page: rootDocsPerPage, total: 0, last_page: 1 });
      setRootDocsContext(data?.context || null);
    } catch (e) {
      setRootDocs([]);
      setRootDocsContext(null);
      setRootDocsPagination({ page: rootDocsPage, per_page: rootDocsPerPage, total: 0, last_page: 1 });
      setRootDocsError(e?.response?.data?.error || 'Failed to load media');
    } finally {
      setRootDocsLoading(false);
    }
  }, [debouncedSearch, isDocumentsTypeNode, rootDocsPage, rootDocsPerPage]);

  const loadFolderDocuments = useCallback(async () => {
    if (!isRealFolderNode) {
      setFolderDocs([]);
      setFolderDocsPagination({ page: 1, per_page: folderDocsPerPage, total: 0, last_page: 1 });
      setFolderDocsContext(null);
      setFolderDocsError('');
      return;
    }

    setFolderDocsLoading(true);
    setFolderDocsError('');
    try {
      const params = {
        page: folderDocsPage,
        per_page: folderDocsPerPage,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        sort: 'created_at',
        direction: 'desc',
      };
      const { data } = await axios.get(api.folderDocuments(selectedNode.id), { params });
      setFolderDocs(data?.data || []);
      setFolderDocsPagination(data?.pagination || { page: folderDocsPage, per_page: folderDocsPerPage, total: 0, last_page: 1 });
      setFolderDocsContext(data?.context || null);
    } catch (e) {
      setFolderDocs([]);
      setFolderDocsContext(null);
      setFolderDocsPagination({ page: folderDocsPage, per_page: folderDocsPerPage, total: 0, last_page: 1 });
      setFolderDocsError(e?.response?.data?.error || 'Failed to load folder media');
    } finally {
      setFolderDocsLoading(false);
    }
  }, [debouncedSearch, folderDocsPage, folderDocsPerPage, isRealFolderNode, selectedNode?.id]);

  const handleDropDocumentInstances = useCallback(async (folderId, documentInstanceIds) => {
    if (!folderId || !documentInstanceIds || !documentInstanceIds.length) return;
    setError('');
    try {
      await axios.post(api.attachUploadedDocuments(folderId), {
        document_instance_ids: documentInstanceIds,
      });
      await loadFolders();
      await loadPagesForNode(selectedNode);
      await loadFolderDocuments();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to attach media to folder');
    } finally {
      setDragStatus(null);
    }
  }, [loadFolderDocuments, loadFolders, loadPagesForNode, selectedNode]);

  useEffect(() => {
    setFolderDocsPage(1);
  }, [selectedNode?.id]);

  useEffect(() => {
    setRootDocsPage(1);
  }, [selectedNode?.id]);

  useEffect(() => {
    loadFolderDocuments();
  }, [loadFolderDocuments]);

  useEffect(() => {
    loadRootDocuments();
  }, [loadRootDocuments]);

  const handleUploadedDocuments = useCallback(async (docs) => {
    if (!canUploadDocumentsHere) return;
    const folderId = selectedNode?.id;
    if (!folderId) return;
    const ids = (docs || []).map((d) => Number(d?.id)).filter(Boolean);
    if (!ids.length) return;

    setError('');
    try {
      await axios.post(api.attachUploadedDocuments(folderId), {
        document_instance_ids: ids,
      });
      await loadFolders();
      await loadPagesForNode(selectedNode);
      await loadFolderDocuments();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to attach uploaded media to folder');
    }
  }, [canUploadDocumentsHere, loadFolderDocuments, loadFolders, loadPagesForNode, selectedNode]);

  const openFolderDocEditor = useCallback((attachment) => {
    setFolderDocSelected(attachment);
    setFolderDocEditorOpen(true);
  }, []);

  const closeFolderDocEditor = useCallback(() => {
    setFolderDocSelected(null);
    setFolderDocEditorOpen(false);
  }, []);

  const setDocumentsError = useCallback((message) => {
    setFolderDocsError(message || '');
    setRootDocsError(message || '');
  }, []);

  const reloadDocumentsLists = useCallback(async () => {
    await loadFolderDocuments();
    await loadRootDocuments();
  }, [loadFolderDocuments, loadRootDocuments]);

  const saveFolderDoc = useCallback(async (payload) => {
    if (!folderDocSelected?.instance?.id || !folderDocSelected?.id) {
      setDocumentsError('Missing instance context for attachment');
      return;
    }
    setFolderDocSaving(true);
    try {
      const url = API_ENDPOINTS.attachments.update(folderDocSelected.instance.id, folderDocSelected.id);
      await axios.put(url, payload);
      closeFolderDocEditor();
      await reloadDocumentsLists();
    } catch (e) {
      setDocumentsError(e?.response?.data?.message || 'Unable to save attachment');
    } finally {
      setFolderDocSaving(false);
    }
  }, [closeFolderDocEditor, folderDocSelected, reloadDocumentsLists, setDocumentsError]);

  const deleteFolderDoc = useCallback(async (attachment) => {
    if (!attachment?.id) {
      setDocumentsError('Missing attachment id');
      return;
    }

    try {
      // System/root documents node => hard delete (delete the document record).
      if (isDocumentsTypeNode) {
        if (!attachment?.instance?.id) {
          setDocumentsError('Missing instance context for attachment');
          return;
        }
        const url = API_ENDPOINTS.attachments.delete(attachment.instance.id, attachment.id);
        await axios.delete(url);
        await reloadDocumentsLists();
        return;
      }

      // Real (user-created) folder node => unlink only (remove folder relationship).
      if (isRealFolderNode) {
        await axios.post(api.detachDocuments(selectedNode.id), {
          document_instance_ids: [attachment.id],
        });
        await reloadDocumentsLists();
        return;
      }

      setDocumentsError('Unknown delete context');
    } catch (e) {
      setDocumentsError(e?.response?.data?.message || 'Unable to delete attachment');
    }
  }, [isDocumentsTypeNode, isRealFolderNode, reloadDocumentsLists, selectedNode?.id, setDocumentsError]);

  const openFolderDocPreview = useCallback((attachment) => {
    if (!attachment) return;
    setFolderDocPreviewAttachment(attachment);
    setFolderDocPreviewOpen(true);
  }, []);

  const closeFolderDocPreview = useCallback(() => {
    setFolderDocPreviewAttachment(null);
    setFolderDocPreviewOpen(false);
  }, []);

  const handleMenuAction = useCallback(async (action, node) => {
    if (!node || node.isAll) return;
    setError('');

    if (action === 'copy') {
      setFolderClipboard({ mode: 'copy', node });
      return;
    }

    if (action === 'cut') {
      setFolderClipboard({ mode: 'cut', node });
      return;
    }

    if (action === 'paste' && folderClipboard) {
      try {
        if (folderClipboard.mode === 'cut') {
          await axios.post(api.moveFolder(folderClipboard.node.id), { new_parent_id: node.id });
          setFolderClipboard(null);
        } else {
          await axios.post(api.copyFolder(folderClipboard.node.id), { new_parent_id: node.id });
        }
        await loadFolders();
      } catch (e) {
        setError(e?.response?.data?.error || 'Paste failed');
      }
      return;
    }

    if (action === 'new-folder') {
      setFolderEditor({
        mode: 'new',
        parentId: node.id,
        label: `New folder inside: ${node.title || ''}`,
      });
      setFolderEditorTitle('');
      return;
    }

    if (action === 'rename') {
      setFolderEditor({
        mode: 'rename',
        folderId: node.id,
        label: `Rename folder: ${node.title || ''}`,
      });
      setFolderEditorTitle(String(node.title || ''));
    }
  }, [folderClipboard]);
  const editorBasePath = useMemo(() => {
    try {
      const path = String(window.location?.pathname || '');
      return path.startsWith('/dev/') ? '/dev/web-template-editor' : '/web-template-editor';
    } catch {
      return '/web-template-editor';
    }
  }, []);

  const getEditorModeForItem = useCallback((item) => {
    const s = String(item?.kind || item?.subclass || '').toLowerCase();
    if (s === 'template') return 'template';
    if (s === 'component') return 'component';
    return 'page';
  }, []);

  const getWebTemplateEditorUrl = useCallback((item) => {
    if (!item) return '#';
    const mode = getEditorModeForItem(item);
    return `${editorBasePath}/${mode}/${item.id}`;
  }, [editorBasePath, getEditorModeForItem]);

  const handlePageMenuAction = useCallback(async (action, page) => {
    if (!page) return;

    if (String(page.type_id) !== '13') {
      return;
    }

    if (action === 'copy') {
      setPageClipboard({ mode: 'copy', pageIds: [page.id] });
      return;
    }
    if (action === 'cut') {
      setPageClipboard({ mode: 'cut', pageIds: [page.id] });
      return;
    }

    if (action === 'browse') {
      const url = page.url || '';
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    if (action === 'edit') {
      window.open(getWebTemplateEditorUrl(page), '_blank', 'noopener,noreferrer');
    }
  }, [getWebTemplateEditorUrl]);

  const pastePagesIntoSelectedFolder = useCallback(async () => {
    if (!pageClipboard || !pageClipboard.pageIds || !pageClipboard.pageIds.length) return;
    if (!selectedNode || selectedNode.isAll || String(selectedNode.id) === 'all') {
      setError('Select a folder to paste into');
      return;
    }

    try {
      await axios.post(api.moveItemsToFolder, {
        target_folder_id: selectedNode.id,
        item_ids: pageClipboard.pageIds,
      });

      if (pageClipboard.mode === 'cut') {
        setPageClipboard(null);
      }

      await loadFolders();
      await loadPagesForNode(selectedNode);
    } catch (e) {
      setError(e?.response?.data?.error || 'Paste failed');
    }
  }, [loadFolders, loadPagesForNode, pageClipboard, selectedNode]);

  const toggleSelectPage = useCallback((id) => {
    setSelectedPageIds((prev) => {
      if (prev.includes(id)) return prev.filter((pid) => pid !== id);
      return [...prev, id];
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    const selectableIds = (filteredSortedPages || []).map((p) => p.id);

    if (!selectableIds.length) {
      setSelectedPageIds([]);
      return;
    }

    if (selectedPageIds.length === selectableIds.length) {
      setSelectedPageIds([]);
    } else {
      setSelectedPageIds(selectableIds);
    }
  }, [filteredSortedPages, selectedPageIds]);

  const selectedItems = useMemo(() => {
    const set = new Set(selectedPageIds.map((v) => String(v)));
    return (pages || []).filter((p) => set.has(String(p.id)));
  }, [pages, selectedPageIds]);

  const selectedHasPages = useMemo(() => {
    return selectedItems.some((p) => String(p.type_id) === '13');
  }, [selectedItems]);

  const handleBulkActionSubmit = useCallback(async (action, value = null) => {
    try {
      if (action === 'move_folder') {
        await handleDropItems(value, selectedPageIds);
        setBulkActionModal({ isOpen: false, mode: null });
        setSelectedPageIds([]);
        setDeleteConfirmArmed(false);
        return;
      }

      if (action === 'set_template') {
        const pageIdsOnly = selectedItems
          .filter((p) => String(p.type_id) === '13')
          .map((p) => p.id);
        await axios.post('/api/v1/webpages/bulk-action', {
          action,
          ids: pageIdsOnly,
          value,
        });
      } else {
        await axios.post(api.bulkAction, {
          action,
          ids: selectedPageIds,
          value,
        });
      }

      setSelectedPageIds([]);
      setBulkActionModal({ isOpen: false, mode: null });
      setDeleteConfirmArmed(false);
      await loadPagesForNode(selectedNode);
    } catch (e) {
      setError(e?.response?.data?.error || 'Bulk action failed');
    }
  }, [handleDropItems, loadPagesForNode, selectedItems, selectedNode, selectedPageIds]);

  const handleBulkAction = useCallback((action) => {
    if (!selectedPageIds.length) return;

    if (action === 'delete') {
      if (!deleteConfirmArmed) {
        setDeleteConfirmArmed(true);
        return;
      }
      handleBulkActionSubmit('delete');
      return;
    }

    setBulkActionModal({ isOpen: true, mode: action });
  }, [deleteConfirmArmed, handleBulkActionSubmit, selectedPageIds]);

  const typeLabel = useCallback((p) => {
    const s = String(p?.kind || p?.subclass || '').toLowerCase();
    if (s === 'template') return 'Template';
    if (s === 'component') return 'Component';
    if (s === 'page') return 'Page';
    if (s === 'document') return 'Document';
    if (s === 'email') return 'Email';
    if (s === 'report') return 'Report';
    if (s === 'audience') return 'Audience';
    if (s === 'list') return 'List';
    if (s === 'form') return 'Form';
    if (s === 'composite_report') return 'Composite Report';
    if (s === 'campaign') return 'Campaign';
    return 'Object';
  }, []);

  const beginResize = useCallback((e) => {
    if (isNarrow) return;
    e.preventDefault();
    e.stopPropagation();

    const onMove = (ev) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = ev.clientX - rect.left;
      const pct = (x / rect.width) * 100;
      const clamped = Math.min(50, Math.max(25, pct));
      setSplitPct(clamped);
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [isNarrow]);

  const tableColSpan = 9 + (showTagsColumn ? 1 : 0) + (showCreatedColumn ? 1 : 0);

  const panelStyle = useMemo(() => ({ borderRadius: 3 }), []);
  const leftPaneCollapsed = !isNarrow && foldersCollapsed;

  return (
    <div className="container-fluid" style={{ height: '100%', overflow: 'hidden' }}>
      <div ref={containerRef} style={{ height: '100%', paddingTop: 10 }}>
        <div style={{ display: isNarrow ? 'block' : 'flex', height: '100%', overflow: 'hidden' }}>
          <div
            style={{
              width: isNarrow ? '100%' : (leftPaneCollapsed ? 28 : `${splitPct}%`),
              minWidth: isNarrow ? 'auto' : (leftPaneCollapsed ? 28 : 280),
              maxWidth: isNarrow ? 'auto' : (leftPaneCollapsed ? 28 : 520),
              height: isNarrow ? 'auto' : '100%',
              overflowY: isNarrow ? 'visible' : (leftPaneCollapsed ? 'hidden' : 'auto'),
              paddingRight: isNarrow ? 0 : (leftPaneCollapsed ? 0 : 8),
              marginBottom: isNarrow ? 10 : 0,
              display: isNarrow && foldersCollapsed ? 'none' : 'block',
            }}
          >
            {leftPaneCollapsed ? (
              <div
                className="panel panel-default"
                style={{
                  ...panelStyle,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <button
                  type="button"
                  className="btn btn-default btn-xs"
                  onClick={() => setFoldersCollapsed(false)}
                  title="Show folders"
                  style={{ padding: '4px 6px' }}
                >
                  <i className="fa fa-chevron-right" />
                </button>
              </div>
            ) : (
              <div
                className="panel panel-default"
                style={{
                  ...panelStyle,
                  display: 'flex',
                  flexDirection: 'column',
                  height: isNarrow ? 'auto' : '100%',
                  overflow: 'hidden',
                }}
              >
                <div className="clearfix panel-heading">
                  <h3 className="panel-title pull-left" style={{ paddingTop: 4 }}>
                    Folders
                  </h3>
                  <button
                    type="button"
                    className="btn btn-default btn-xs pull-right"
                    onClick={() => setFoldersCollapsed(true)}
                    title="Hide folders"
                    style={{ marginLeft: 8 }}
                  >
                    <i className={isNarrow ? 'fa fa-times' : 'fa fa-chevron-left'} />
                  </button>
                  <button
                    type="button"
                    className="btn btn-default btn-xs pull-right"
                    onClick={openNewRootFolderEditor}
                    title="New root folder"
                    style={{ marginLeft: 8 }}
                  >
                    <i className="fa fa-plus" /> New Root Folder
                  </button>
                </div>
                <div className="panel-body" style={{ flex: '1 1 auto', overflowY: 'auto', overflowX: 'hidden' }}>
                  {loadingFolders ? <div className="text-muted">Loading folders…</div> : null}

                  {folderEditor ? (
                    <div className="well well-sm" style={{ marginBottom: 10 }}>
                      <div className="form-group" style={{ marginBottom: 6 }}>
                        <div style={{ fontWeight: 'bold' }}>{folderEditor.mode === 'new' ? 'New Folder' : 'Rename Folder'}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>{folderEditor.label}</div>
                      </div>
                      <div className="input-group input-group-sm">
                        <input
                          ref={folderEditorInputRef}
                          type="text"
                          className="form-control input-sm"
                          value={folderEditorTitle}
                          disabled={folderEditorBusy}
                          onChange={(e) => setFolderEditorTitle(e.target.value)}
                          placeholder={folderEditor.mode === 'new' ? 'Folder name' : 'New folder name'}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              closeFolderEditor();
                            }
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              submitFolderEditor();
                            }
                          }}
                        />
                        <span className="input-group-btn">
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={folderEditorBusy || !(folderEditorTitle || '').trim()}
                            onClick={submitFolderEditor}
                          >
                            {folderEditorBusy ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-default btn-sm"
                            disabled={folderEditorBusy}
                            onClick={closeFolderEditor}
                          >
                            Cancel
                          </button>
                        </span>
                      </div>
                    </div>
                  ) : null}

                  <WebpagesFolderTree
                    folders={folders}
                    selectedKey={selectedKeyValue}
                    onSelect={handleSelect}
                    onContextMenu={(e, node) => setMenuState({ visible: true, x: e.clientX, y: e.clientY, node })}
                    onDropItems={handleDropItems}
                    onDropDocumentInstances={handleDropDocumentInstances}
                    onDragHover={handleDragHover}
                  />
                </div>
              </div>
            )}
          </div>

          {!isNarrow && !foldersCollapsed ? (
            <div
              onMouseDown={beginResize}
              title="Drag to resize"
              style={{ width: 10, cursor: 'col-resize', height: '100%', position: 'relative', flex: '0 0 auto' }}
            >
              <div style={{ position: 'absolute', top: 0, bottom: 0, left: 4, width: 2, backgroundColor: '#ddd' }} />
            </div>
          ) : null}

          <div style={{ flex: '1 1 auto', height: '100%', overflow: 'hidden' }}>
            <div
              className="panel panel-default"
              style={{ ...panelStyle, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
            >
              <div className="clearfix panel-heading">
                <h3 className="panel-title pull-left" style={{ paddingTop: 4 }}>
                  Objects
                  <span className="text-muted" style={{ fontSize: 14, marginLeft: 10 }}>
                    ({selectedNode?.title || 'All'})
                  </span>
                </h3>
                {isNarrow || foldersCollapsed ? (
                  <button
                    type="button"
                    className="btn btn-default btn-xs pull-right"
                    onClick={() => setFoldersCollapsed((v) => !v)}
                    title="Toggle folders"
                  >
                    <i className="fa fa-folder-open-o" />
                  </button>
                ) : null}
              </div>

              <div
                className="panel-body"
                style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}
                onContextMenu={(e) => {
                  if (!pageClipboard || !pageClipboard.pageIds?.length) return;
                  if (!selectedNode || selectedNode.isAll || String(selectedNode.id) === 'all') return;
                  if (e.target && e.target.closest && (e.target.closest('tr') || e.target.closest('[data-item-card="1"]'))) return;
                  e.preventDefault();
                  e.stopPropagation();
                  setPagesPanelMenuState({ visible: true, x: e.clientX, y: e.clientY });
                }}
              >
                {error ? <div className="alert alert-danger">{error}</div> : null}
                {loadingPages ? <div className="text-muted">Loading items…</div> : null}

                {showInstancesTab ? (
                  <ul className="nav nav-tabs" style={{ marginBottom: 10 }}>
                    <li className={rightTab === 'definitions' ? 'active' : ''}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setRightTab('definitions');
                        }}
                        style={{ padding: '4px 10px', fontSize: 12, lineHeight: '16px' }}
                      >
                        Definitions
                      </a>
                    </li>
                    <li className={rightTab === 'instances' ? 'active' : ''}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setRightTab('instances');
                        }}
                        style={{ padding: '4px 10px', fontSize: 12, lineHeight: '16px' }}
                      >
                        Instances
                      </a>
                    </li>
                  </ul>
                ) : null}

                {(!showInstancesTab || rightTab === 'definitions') ? (
                  <>
                {selectedPageIds.length > 0 ? (
                  <div className="clearfix alert alert-info" style={{ padding: '8px 15px', marginBottom: 10 }}>
                    <span className="pull-left" style={{ paddingTop: 4 }}>
                      {selectedPageIds.length} items selected
                    </span>
                    <div className="btn-group btn-group-xs pull-right">
                      {!deleteConfirmArmed ? (
                        <button className="btn btn-danger" onClick={() => handleBulkAction('delete')}>
                          <i className="fa fa-trash" /> Delete
                        </button>
                      ) : (
                        <>
                          <button className="btn btn-danger" onClick={() => handleBulkAction('delete')}>
                            Confirm delete
                          </button>
                          <button className="btn btn-default" onClick={() => setDeleteConfirmArmed(false)}>
                            Cancel
                          </button>
                        </>
                      )}
                      <button className="btn btn-default" onClick={() => handleBulkAction('add_tag')}>
                        <i className="fa fa-tag" /> Add Tag
                      </button>
                      <button className="btn btn-default" onClick={() => handleBulkAction('remove_tag')}>
                        <i className="fa fa-eraser" /> Remove Tag
                      </button>
                      {selectedHasPages ? (
                        <button className="btn btn-default" onClick={() => handleBulkAction('set_template')}>
                          <i className="fa fa-file-code-o" /> Set Template
                        </button>
                      ) : null}
                      <button className="btn btn-default" onClick={() => handleBulkAction('move_folder')}>
                        <i className="fa fa-folder-open-o" /> Move to Folder
                      </button>
                    </div>
                  </div>
                ) : null}

                <div className="row" style={{ marginBottom: 10 }}>
                  <div className="col-sm-6">
                    <input
                      type="text"
                      className="form-control input-sm"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Filter by title, TID, type, or tag"
                    />
                  </div>
                  <div className="text-right col-sm-6">
                    <div className="form-group" style={{ marginBottom: 2 }}>
                      <label className="checkbox-inline">
                        <input
                          type="checkbox"
                          checked={showTagsColumn}
                          onChange={(e) => setShowTagsColumn(e.target.checked)}
                        />
                        Show tags
                      </label>
                      <label className="checkbox-inline">
                        <input
                          type="checkbox"
                          checked={showCreatedColumn}
                          onChange={(e) => setShowCreatedColumn(e.target.checked)}
                        />
                        Show created
                      </label>

                      <span className="btn-group btn-group-xs" style={{ marginLeft: 10 }}>
                        <button
                          type="button"
                          className={`btn btn-default ${itemsView === 'list' ? 'active' : ''}`}
                          onClick={() => setItemsView('list')}
                          title="List view"
                        >
                          <i className="fa fa-th-list" />
                        </button>
                        <button
                          type="button"
                          className={`btn btn-default ${itemsView === 'grid' ? 'active' : ''}`}
                          onClick={() => setItemsView('grid')}
                          title="Grid view"
                        >
                          <i className="fa fa-th" />
                        </button>
                      </span>
                    </div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                        Drag rows to move items into folders
                    </div>
                  </div>
                </div>

                {itemsView === 'grid' ? (
                  <div className="row" style={{ marginTop: 5 }}>
                    {filteredSortedPages && filteredSortedPages.length ? filteredSortedPages.map((p) => {
                      const previewUrl = p.preview_url || null;
                      const isPage = String(p.type_id) === '13';
                      const editorMode = getEditorModeForItem(p);
                      const showLegacyEditor = editorMode === 'page';
                      return (
                        <div key={String(p.id)} className="col-xs-12 col-sm-6 col-md-4 col-lg-3" style={{ marginBottom: 12 }}>
                          <div
                            className="thumbnail"
                            data-item-card="1"
                            draggable
                            onDragStart={(e) => handleDragStartItem(e, p)}
                            onDragEnd={handleDragEnd}
                            onContextMenu={(e) => {
                              if (!isPage) return;
                              e.preventDefault();
                              e.stopPropagation();
                              setPageMenuState({ visible: true, x: e.clientX, y: e.clientY, page: p });
                            }}
                            style={{ cursor: 'grab' }}
                          >
                            <div style={{ height: 140, overflow: 'hidden', backgroundColor: '#f7f7f7' }}>
                              {previewUrl ? (
                                <img
                                  src={previewUrl}
                                  alt=""
                                  className="img-responsive"
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={(e) => {
                                    try {
                                      e.currentTarget.style.display = 'none';
                                    } catch {
                                      // ignore
                                    }
                                  }}
                                />
                              ) : (
                                <div className="text-center text-muted" style={{ paddingTop: 45 }}>
                                  <i className="fa fa-file-o fa-2x" />
                                </div>
                              )}
                            </div>
                            <div className="caption" style={{ paddingTop: 8 }}>
                              <div className="clearfix" style={{ marginBottom: 6 }}>
                                <label className="checkbox-inline" style={{ margin: 0 }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedPageIds.includes(p.id)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      toggleSelectPage(p.id);
                                    }}
                                  />
                                </label>
                                <span className="pull-right btn-group-xs"  role="group" style={{ padding: 0 }}>
                                  {isPage ? (
                                    <>
                                      <a
                                        href={p.url || '#'}
                                        className="btn btn-primary btn-xs"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Browse"
                                        onClick={(e) => {
                                          if (!p.url) e.preventDefault();
                                        }}
                                      >
                                        <i className="ti-eye" />
                                      </a>
                                      <a
                                        href={getWebTemplateEditorUrl(p)}
                                        className="btn btn-primary btn-xs"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Edit"
                                        style={{ marginLeft: 0 }}
                                      >
                                        <i className="fa fa-object-group" />
                                      </a>
                                      {showLegacyEditor ? (
                                        <a
                                          href={`/webpages/edit_cb/${p.id}`}
                                          className="btn btn-primary btn-xs"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          title="Legacy editor"
                                          style={{ marginLeft: 0 }}
                                        >
                                          <i className="fa fa-file-text-o" />
                                        </a>
                                      ) : null}
                                    </>
                                  ) : null}
                                </span>
                              </div>

                              <div style={{ fontWeight: 'bold', wordBreak: 'break-word' }}>{p.title}</div>
                              <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                                <span>{typeLabel(p)}</span>
                                {p.tid ? <span> · <code>{p.tid}</code></span> : null}
                              </div>

                              {p.template_title ? (
                                <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                                  Template: <span title={p.template_tid || ''}>{p.template_title}</span>
                                </div>
                              ) : null}

                              {showTagsColumn ? (
                                <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                                  Tags: {p.tags ? <span>{p.tags}</span> : <span>—</span>}
                                </div>
                              ) : null}

                              {showCreatedColumn ? (
                                <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                                  Created: {formatDateTime(p.created_at) || '—'}
                                </div>
                              ) : null}

                              <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                                Updated: {formatDateTime(p.updated_at) || '—'}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }) : (
                      <div className="col-xs-12 text-muted">No items</div>
                    )}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped table-condensed">
                      <thead>
                        <tr>
                          <th style={{ width: 30 }}>
                            <input
                              type="checkbox"
                              checked={filteredSortedPages.length > 0 && selectedPageIds.length === filteredSortedPages.length}
                              onChange={toggleSelectAll}
                            />
                          </th>
                          <th style={{ width: 28 }}></th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('title')}>Title</th>
                          <th>Type</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('tid')}>TID</th>
                          <th>Template</th>
                          {showTagsColumn ? <th>Tags</th> : null}
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('active')}>Active</th>
                          {showCreatedColumn ? <th>Created</th> : null}
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('updated_at')}>Updated</th>
                          <th style={{ width: 120 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSortedPages && filteredSortedPages.length ? filteredSortedPages.map((p) => (
                          <tr
                            key={String(p.id)}
                            draggable
                            onDragStart={(e) => handleDragStartItem(e, p)}
                            onDragEnd={handleDragEnd}
                            onContextMenu={(e) => {
                              if (String(p.type_id) !== '13') return;
                              e.preventDefault();
                              e.stopPropagation();
                              setPageMenuState({ visible: true, x: e.clientX, y: e.clientY, page: p });
                            }}
                            style={{ cursor: 'grab' }}
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedPageIds.includes(p.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleSelectPage(p.id);
                                }}
                              />
                            </td>
                            <td>
                              <i className="fa fa-bars text-muted" title="Drag to move" aria-hidden="true" />
                            </td>
                            <td>{p.title}</td>
                            <td>{typeLabel(p)}</td>
                            <td><code>{p.tid}</code></td>
                            <td>
                              {p.template_title ? (
                                <span title={p.template_tid || ''}>{p.template_title}</span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            {showTagsColumn ? (
                              <td>{p.tags ? <span>{p.tags}</span> : <span className="text-muted">—</span>}</td>
                            ) : null}
                            <td>{String(p.active) === '1' ? 'Yes' : 'No'}</td>
                            {showCreatedColumn ? <td>{formatDateTime(p.created_at)}</td> : null}
                            <td>{formatDateTime(p.updated_at)}</td>
                            <td className="text-right btn-group-xs" role="group"  style={{ padding: 0 }}>
                              {String(p.type_id) === '13' ? (
                                <>
                                  <a
                                    href={p.url || '#'}
                                    className="btn btn-primary btn-xs"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Browse"
                                    onClick={(e) => {
                                      if (!p.url) e.preventDefault();
                                    }}
                                  >
                                    <i className="ti-eye" />
                                  </a>
                                  <a
                                    href={getWebTemplateEditorUrl(p)}
                                    className="btn btn-primary btn-xs"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Edit"
                                    style={{ marginLeft: 0 }}
                                  >
                                    <i className="fa fa-object-group" />
                                  </a>
                                  {getEditorModeForItem(p) === 'page' ? (
                                    <a
                                      href={`/webpages/edit_cb/${p.id}`}
                                      className="btn btn-primary btn-xs"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title="Legacy editor"
                                      style={{ marginLeft: 0 }}
                                    >
                                      <i className="fa fa-file-text-o" />
                                    </a>
                                  ) : null}
                                </>
                              ) : null}
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan={tableColSpan} className="text-muted">No items</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                  </>
                ) : null}

                {(showInstancesTab && rightTab === 'instances') ? (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
                      <div className="clearfix" style={{ marginBottom: 8, flex: '0 0 auto' }}>
                        <div className="pull-left text-muted" style={{ fontSize: 12, paddingTop: 4 }}>
                          Drag media into folders to attach
                        </div>
                        <div className="pull-right">
                          <span className="btn-group btn-group-xs" style={{ marginLeft: 10 }}>
                            <button
                              type="button"
                              className={`btn btn-default ${docsView === 'list' ? 'active' : ''}`}
                              onClick={() => setDocsView('list')}
                              title="List view"
                            >
                              <i className="fa fa-th-list" />
                            </button>
                            <button
                              type="button"
                              className={`btn btn-default ${docsView === 'grid' ? 'active' : ''}`}
                              onClick={() => setDocsView('grid')}
                              title="Grid view"
                            >
                              <i className="fa fa-th" />
                            </button>
                          </span>
                        </div>
                      </div>

                    {isRealFolderNode ? (
                  <div
                    className="panel panel-default"
                    style={{ ...panelStyle, marginTop: 10, flex: '1 1 auto', minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
                  >
                    <div className="clearfix panel-heading" style={{ paddingTop: 4, flex: '0 0 auto' }}>
                      <h4 className="panel-title pull-left" style={{ paddingTop: 4 }}>
                        Media
                        <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
                          (instances: attached + child)
                        </span>
                      </h4>
                      <div className="pull-right" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <select
                          className="form-control input-sm"
                          value={folderDocsPerPage}
                          onChange={(e) => {
                            setFolderDocsPerPage(Number(e.target.value));
                            setFolderDocsPage(1);
                          }}
                          style={{ width: 120 }}
                        >
                          {[10, 25, 50].map((size) => (
                            <option key={size} value={size}>{size} / page</option>
                          ))}
                        </select>
                        <button type="button" className="btn btn-default btn-xs" onClick={loadFolderDocuments}>
                          Refresh
                        </button>
                      </div>
                    </div>
                    <div className="panel-body" style={{ flex: '1 1 auto', overflow: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                      {folderDocsError ? <div className="alert alert-danger">{folderDocsError}</div> : null}

                      {canUploadDocumentsHere ? (
                        <div className="well well-sm" style={{ marginBottom: 10, flex: '0 0 auto' }}>
                          <div className="clearfix" style={{ marginBottom: 6 }}>
                            <div className="pull-left" style={{ fontWeight: 'bold' }}>Upload media to folder</div>
                            <div className="pull-right text-muted" style={{ fontSize: 12 }}>{selectedNode?.title || ''}</div>
                          </div>
                          {uploadInfo.loading ? (
                            <div className="text-muted">Loading uploader…</div>
                          ) : uploadInfo.templateId && uploadInfo.folderDefinitionId ? (
                            <FileUploader
                              templateId={uploadInfo.templateId}
                              folderDefinitionId={uploadInfo.folderDefinitionId}
                              onComplete={handleUploadedDocuments}
                            />
                          ) : (
                            <div className="text-muted">Uploader unavailable (missing upload bucket)</div>
                          )}
                        </div>
                      ) : null}

                      {folderDocsLoading ? (
                        <div className="text-muted" style={{ flex: '1 1 auto' }}>Loading media…</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
                          {docsView === 'grid' ? (
                            <div className="row" style={{ marginTop: 5, flex: '1 1 auto', overflow: 'auto', minHeight: 0 }}>
                              {(folderDocs || []).length ? (folderDocs || []).map((a) => {
                                const mediaType = getAttachmentMediaType(a);
                                const thumb = getAttachmentPreviewUrl(a);
                                return (
                                  <div key={String(a.id)} className="col-xs-12 col-sm-6 col-md-4 col-lg-3" style={{ marginBottom: 16 }}>
                                    <div
                                      className="thumbnail"
                                      draggable
                                      onDragStart={(e) => handleDragStartDocumentInstance(e, a)}
                                      onDragEnd={handleDragEnd}
                                      style={{ cursor: 'grab', borderRadius: 3 }}
                                    >
                                      <div style={{ height: 105, overflow: 'hidden', backgroundColor: '#f7f7f7', borderRadius: 3 }}>
                                        {mediaType === 'image' && thumb ? (
                                          <img
                                            src={thumb}
                                            alt=""
                                            className="img-responsive"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            onError={(e) => {
                                              try {
                                                e.currentTarget.style.display = 'none';
                                              } catch {
                                                // ignore
                                              }
                                            }}
                                          />
                                        ) : (
                                          <div className="text-center text-muted" style={{ paddingTop: 30 }}>
                                            <i className="fa fa-file-o fa-2x" />
                                          </div>
                                        )}
                                      </div>
                                      <div className="caption" style={{ paddingTop: 6 }}>
                                        <div style={{ fontWeight: 'bold', fontSize: 13, wordBreak: 'break-word' }}>{a.title || `Attachment #${a.id}`}</div>
                                        <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>{a.mime_type || 'Unknown type'}</div>
                                        <div style={{ marginTop: 5 }}>
                                          <button type="button" className="btn btn-default btn-xs" onClick={() => openFolderDocEditor(a)}>
                                            Edit
                                          </button>
                                          <button type="button" className="btn btn-link btn-xs" onClick={() => openFolderDocPreview(a)}>
                                            {mediaType === 'video' ? 'Play' : 'Preview'}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }) : (
                                <div className="col-xs-12 text-muted">No media</div>
                              )}
                            </div>
                          ) : (
                            <div className="table-responsive" style={{ flex: '1 1 auto', overflow: 'auto', minHeight: 0 }}>
                              <AttachmentListView
                                items={folderDocs || []}
                                onEdit={openFolderDocEditor}
                                onDelete={deleteFolderDoc}
                                onPreview={openFolderDocPreview}
                                showInstance
                                draggable
                                onDragStart={handleDragStartDocumentInstance}
                                onDragEnd={handleDragEnd}
                                wrapResponsive={false}
                                deleteConfirmPrompt="Confirm unlink?"
                              />
                            </div>
                          )}
                          <div className="clearfix" style={{ marginTop: 8 }}>
                            <div className="pull-left text-muted" style={{ fontSize: 12 }}>
                              Total: {folderDocsPagination?.total || 0}
                              {folderDocsContext?.folder?.instance_id ? (
                                <span style={{ marginLeft: 8 }}>
                                  Folder instance #{folderDocsContext.folder.instance_id}
                                </span>
                              ) : null}
                            </div>
                            <div className="pull-right btn-group btn-group-xs">
                              <button
                                type="button"
                                className="btn btn-default"
                                disabled={(folderDocsPagination?.page || 1) <= 1}
                                onClick={() => setFolderDocsPage((p) => Math.max(1, p - 1))}
                              >
                                Prev
                              </button>
                              <button type="button" className="btn btn-default" disabled>
                                Page {folderDocsPagination?.page || 1} / {folderDocsPagination?.last_page || 1}
                              </button>
                              <button
                                type="button"
                                className="btn btn-default"
                                disabled={(folderDocsPagination?.page || 1) >= (folderDocsPagination?.last_page || 1)}
                                onClick={() => setFolderDocsPage((p) => p + 1)}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                    ) : null}

                    {isDocumentsTypeNode ? (
                  <div
                    className="panel panel-default"
                    style={{ ...panelStyle, marginTop: 10, flex: '1 1 auto', minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
                  >
                    <div className="clearfix panel-heading" style={{ paddingTop: 4, flex: '0 0 auto' }}>
                      <h4 className="panel-title pull-left" style={{ paddingTop: 4 }}>
                        Media
                        <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
                          (all instances)
                        </span>
                      </h4>
                      <div className="pull-right" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <select
                          className="form-control input-sm"
                          value={rootDocsPerPage}
                          onChange={(e) => {
                            setRootDocsPerPage(Number(e.target.value));
                            setRootDocsPage(1);
                          }}
                          style={{ width: 120 }}
                        >
                          {[10, 25, 50].map((size) => (
                            <option key={size} value={size}>{size} / page</option>
                          ))}
                        </select>
                        <button type="button" className="btn btn-default btn-xs" onClick={loadRootDocuments}>
                          Refresh
                        </button>
                      </div>
                    </div>
                    <div className="panel-body" style={{ flex: '1 1 auto', overflow: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                      {rootDocsError ? <div className="alert alert-danger">{rootDocsError}</div> : null}

                      {rootDocsLoading ? (
                        <div className="text-muted" style={{ flex: '1 1 auto' }}>Loading media…</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
                          {docsView === 'grid' ? (
                            <div className="row" style={{ marginTop: 5, flex: '1 1 auto', overflow: 'auto', minHeight: 0 }}>
                              {(rootDocs || []).length ? (rootDocs || []).map((a) => {
                                const mediaType = getAttachmentMediaType(a);
                                const thumb = getAttachmentPreviewUrl(a);
                                return (
                                  <div key={String(a.id)} className="col-xs-12 col-sm-6 col-md-4 col-lg-3" style={{ marginBottom: 16 }}>
                                    <div
                                      className="thumbnail"
                                      draggable
                                      onDragStart={(e) => handleDragStartDocumentInstance(e, a)}
                                      onDragEnd={handleDragEnd}
                                      style={{ cursor: 'grab', borderRadius: 3 }}
                                    >
                                      <div style={{ height: 105, overflow: 'hidden', backgroundColor: '#f7f7f7', borderRadius: 3 }}>
                                        {mediaType === 'image' && thumb ? (
                                          <img
                                            src={thumb}
                                            alt=""
                                            className="img-responsive"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            onError={(e) => {
                                              try {
                                                e.currentTarget.style.display = 'none';
                                              } catch {
                                                // ignore
                                              }
                                            }}
                                          />
                                        ) : (
                                          <div className="text-center text-muted" style={{ paddingTop: 30 }}>
                                            <i className="fa fa-file-o fa-2x" />
                                          </div>
                                        )}
                                      </div>
                                      <div className="caption" style={{ paddingTop: 6 }}>
                                        <div style={{ fontWeight: 'bold', fontSize: 13, wordBreak: 'break-word' }}>{a.title || `Attachment #${a.id}`}</div>
                                        <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>{a.mime_type || 'Unknown type'}</div>
                                        <div style={{ marginTop: 5 }}>
                                          <button type="button" className="btn btn-default btn-xs" onClick={() => openFolderDocEditor(a)}>
                                            Edit
                                          </button>
                                          <button type="button" className="btn btn-link btn-xs" onClick={() => openFolderDocPreview(a)}>
                                            {mediaType === 'video' ? 'Play' : 'Preview'}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }) : (
                                <div className="col-xs-12 text-muted">No media</div>
                              )}
                            </div>
                          ) : (
                            <div className="table-responsive" style={{ flex: '1 1 auto', overflow: 'auto', minHeight: 0 }}>
                              <AttachmentListView
                                items={rootDocs || []}
                                onEdit={openFolderDocEditor}
                                onDelete={deleteFolderDoc}
                                onPreview={openFolderDocPreview}
                                showInstance
                                draggable
                                onDragStart={handleDragStartDocumentInstance}
                                onDragEnd={handleDragEnd}
                                wrapResponsive={false}
                              />
                            </div>
                          )}
                          <div className="clearfix" style={{ marginTop: 8 }}>
                            <div className="pull-left text-muted" style={{ fontSize: 12 }}>
                              Total: {rootDocsPagination?.total || 0}
                            </div>
                            <div className="pull-right btn-group btn-group-xs">
                              <button
                                type="button"
                                className="btn btn-default"
                                disabled={(rootDocsPagination?.page || 1) <= 1}
                                onClick={() => setRootDocsPage((p) => Math.max(1, p - 1))}
                              >
                                Prev
                              </button>
                              <button type="button" className="btn btn-default" disabled>
                                Page {rootDocsPagination?.page || 1} / {rootDocsPagination?.last_page || 1}
                              </button>
                              <button
                                type="button"
                                className="btn btn-default"
                                disabled={(rootDocsPagination?.page || 1) >= (rootDocsPagination?.last_page || 1)}
                                onClick={() => setRootDocsPage((p) => p + 1)}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                    ) : null}
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {menuState.visible ? (
        <WebpagesFolderContextMenu
          x={menuState.x}
          y={menuState.y}
          node={menuState.node}
          clipboard={folderClipboard}
          allowPaste={!!folderClipboard && menuState.node && String(folderClipboard.node?.id) !== String(menuState.node?.id)}
          onClose={() => setMenuState({ visible: false, x: 0, y: 0, node: null })}
          onAction={handleMenuAction}
        />
      ) : null}

      {pageMenuState.visible ? (
        <WebpagesPageContextMenu
          x={pageMenuState.x}
          y={pageMenuState.y}
          page={pageMenuState.page}
          onClose={() => setPageMenuState({ visible: false, x: 0, y: 0, page: null })}
          onAction={handlePageMenuAction}
        />
      ) : null}

      {pagesPanelMenuState.visible ? (
        <WebpagesPagesPanelContextMenu
          x={pagesPanelMenuState.x}
          y={pagesPanelMenuState.y}
          allowPaste={!!pageClipboard && !!pageClipboard.pageIds?.length && !selectedNode?.isAll && String(selectedNode?.id) !== 'all'}
          onClose={() => setPagesPanelMenuState({ visible: false, x: 0, y: 0 })}
          onAction={async (action) => {
            if (action === 'paste') {
              await pastePagesIntoSelectedFolder();
            }
          }}
        />
      ) : null}

      {dragStatus ? (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 30,
            backgroundColor: '#337ab7',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            padding: '0 15px',
            fontSize: 13,
            zIndex: 1000,
          }}
        >
          <span style={{ fontWeight: 'bold', marginRight: 5 }}>{dragStatus.subject}</span>
          <span style={{ marginRight: 5 }}>{dragStatus.action}</span>
          <span style={{ fontWeight: 'bold' }}>{dragStatus.target}</span>
        </div>
      ) : null}

      <WebpagesBulkActionsModal
        isOpen={bulkActionModal.isOpen}
        mode={bulkActionModal.mode}
        folders={folders}
        onClose={() => setBulkActionModal({ isOpen: false, mode: null })}
        onSubmit={(value) => handleBulkActionSubmit(bulkActionModal.mode, value)}
      />

      <AttachmentEditor
        attachment={folderDocSelected}
        open={folderDocEditorOpen}
        onClose={closeFolderDocEditor}
        onSave={saveFolderDoc}
        busy={folderDocSaving}
      />

      <AttachmentPreviewModal
        attachment={folderDocPreviewAttachment}
        open={folderDocPreviewOpen}
        onClose={closeFolderDocPreview}
      />
    </div>
  );
}
